import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { api } from "../../services/api";

import { useNavigate } from "react-router-dom";

// Tab panel component for switching between categories and resources
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ResourceManagementPage = () => {
  // State for tabs
  const [tabValue, setTabValue] = useState(0);

  // State for categories
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    description: "",
  });
  const [categoryErrors, setCategoryErrors] = useState({});

  // State for resources
  const [resources, setResources] = useState([]);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceFormData, setResourceFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    file_path: "",
  });
  const [resourceErrors, setResourceErrors] = useState({});

  // State for loading and notifications
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Load data
  useEffect(() => {
    fetchCategories();
    fetchResources();
  }, []);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get("/resources/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      showNotification("Failed to load categories", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch resources
  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await api.get("/resources");
      setResources(response.data);
    } catch (error) {
      console.error("Error fetching resources:", error);
      showNotification("Failed to load resources", "error");
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Category dialog functions
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name,
        description: category.description || "",
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name: "",
        description: "",
      });
    }
    setCategoryErrors({});
    setCategoryDialogOpen(true);
  };

  const closeCategoryDialog = () => {
    setCategoryDialogOpen(false);
  };

  const handleCategoryFormChange = (e) => {
    const { name, value } = e.target;
    setCategoryFormData({ ...categoryFormData, [name]: value });
    // Clear error for this field when user types
    if (categoryErrors[name]) {
      setCategoryErrors({ ...categoryErrors, [name]: "" });
    }
  };

  const validateCategoryForm = () => {
    const errors = {};
    if (!categoryFormData.name.trim()) {
      errors.name = "Category name is required";
    }
    setCategoryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveCategory = async () => {
    if (!validateCategoryForm()) return;

    try {
      setLoading(true);
      if (editingCategory) {
        // Update existing category
        await api.put(
          `/resources/categories/${editingCategory.id}`,
          categoryFormData,
        );
        showNotification("Category updated successfully");
      } else {
        // Create new category
        await api.post("/resources/categories", categoryFormData);
        showNotification("Category created successfully");
      }
      closeCategoryDialog();
      fetchCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      showNotification(
        error.response?.data?.message || "Failed to save category",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/resources/categories/${categoryId}`);
      showNotification("Category deleted successfully");
      fetchCategories();
      // Refresh resources to update UI in case category was used
      fetchResources();
    } catch (error) {
      console.error("Error deleting category:", error);
      if (error.response?.status === 400) {
        showNotification(
          "Cannot delete category because it has associated resources",
          "error",
        );
      } else {
        showNotification(
          error.response?.data?.message || "Failed to delete category",
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Get file name from path
  const getFileName = (filePath) => {
    if (!filePath) return "";
    // Split by both forward and backward slashes to handle different path formats
    const parts = filePath.split(/[\\\/]/);
    return parts[parts.length - 1];
  };

  // Resource dialog functions
  const openResourceDialog = (resource = null) => {
    if (resource) {
      setEditingResource(resource);
      setResourceFormData({
        name: resource.name,
        description: resource.description || "",
        category_id: resource.category_id || "",
        file_path: resource.file_path ? getFileName(resource.file_path) : "",
      });
    } else {
      setEditingResource(null);
      setResourceFormData({
        name: "",
        description: "",
        category_id: "",
        file_path: "",
      });
    }
    setResourceErrors({});
    setResourceDialogOpen(true);
  };

  const closeResourceDialog = () => {
    setResourceDialogOpen(false);
  };

  const handleResourceFormChange = (e) => {
    const { name, value } = e.target;
    setResourceFormData({ ...resourceFormData, [name]: value });
    // Clear error for this field when user types
    if (resourceErrors[name]) {
      setResourceErrors({ ...resourceErrors, [name]: "" });
    }
  };

  const validateResourceForm = () => {
    const errors = {};
    if (!resourceFormData.name.trim()) {
      errors.name = "Resource name is required";
    }
    if (!editingResource && !resourceFormData.file_path.trim()) {
      errors.file_path = "File path is required";
    }
    setResourceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveResource = async () => {
    if (!validateResourceForm()) return;

    try {
      setLoading(true);

      if (editingResource) {
        // Update existing resource (no file path change)
        await api.put(`/resources/${editingResource.id}`, {
          name: resourceFormData.name,
          description: resourceFormData.description,
          category_id: resourceFormData.category_id || null,
        });
        showNotification("Resource updated successfully");
      } else {
        // Create new resource with file reference
        await api.post("/resources", {
          name: resourceFormData.name,
          description: resourceFormData.description,
          file_path: resourceFormData.file_path,
          category_id: resourceFormData.category_id || null,
        });
        showNotification("Resource created successfully");
      }

      closeResourceDialog();
      fetchResources();
    } catch (error) {
      console.error("Error saving resource:", error);
      showNotification(
        error.response?.data?.message || "Failed to save resource",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteResource = async (resourceId) => {
    if (!window.confirm("Are you sure you want to delete this resource?")) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/resources/${resourceId}`);
      showNotification("Resource deleted successfully");
      fetchResources();
    } catch (error) {
      console.error("Error deleting resource:", error);
      showNotification(
        error.response?.data?.message || "Failed to delete resource",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle resource download
  const downloadResource = async (resource) => {
    try {
      setLoading(true);
      // Start download process
      const response = await api.post("/resources/start-download", {
        resourceId: resource.id,
      });

      if (response.data && response.data.downloadUrl) {
        // Open the download URL in a new tab
        window.open(response.data.downloadUrl, "_blank");
      } else {
        showNotification("Failed to generate download link", "error");
      }
    } catch (error) {
      console.error("Error initiating download:", error);
      showNotification(
        error.response?.data?.message || "Failed to download resource",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Get category name by id
  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Uncategorized";
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Resource Management
      </Typography>

      <Paper elevation={3} sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="resource management tabs"
          centered
        >
          <Tab label="Resources" />
          <Tab label="Categories" />
        </Tabs>

        {/* Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openResourceDialog()}
            >
              Add Resource
            </Button>
          </Box>

          {loading && resources.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : resources.length === 0 ? (
            <Typography align="center" sx={{ p: 3 }}>
              No resources found. Click "Add Resource" to create one.
            </Typography>
          ) : (
            <TableContainer elevation={1}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>File Type</TableCell>
                    <TableCell>File Size</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resources.map((resource, index) => (
                    <TableRow
                      key={resource.id}
                      hover
                      sx={{
                        "&:hover": {
                          borderBottomColor: "primary.main",
                        },
                        "& td": {
                          py: 2,
                        },
                      }}
                    >
                      <TableCell>{resource.name}</TableCell>
                      <TableCell>{resource.description}</TableCell>
                      <TableCell>
                        {resource.category
                          ? resource.category.name
                          : "Uncategorized"}
                      </TableCell>
                      <TableCell>{getFileName(resource.file_path)}</TableCell>
                      <TableCell>{resource.file_type}</TableCell>
                      <TableCell>
                        {Math.round(resource.file_size / 1024)} KB
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => openResourceDialog(resource)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deleteResource(resource.id)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          color="success"
                          onClick={() => downloadResource(resource)}
                          title="Download"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openCategoryDialog()}
            >
              Add Category
            </Button>
          </Box>

          {loading && categories.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : categories.length === 0 ? (
            <Typography align="center" sx={{ p: 3 }}>
              No categories found. Click "Add Category" to create one.
            </Typography>
          ) : (
            <TableContainer
              elevation={1}
              sx={{
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Resource Count</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category, index) => (
                    <TableRow
                      key={category.id}
                      hover
                      sx={{
                        "&:hover": {
                          borderBottomColor: "primary.main",
                        },
                        "& td": {
                          py: 2,
                        },
                      }}
                    >
                      <TableCell>{category.name}</TableCell>
                      <TableCell>{category.description}</TableCell>
                      <TableCell>
                        {
                          resources.filter((r) => r.category_id === category.id)
                            .length
                        }
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => openCategoryDialog(category)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deleteCategory(category.id)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Category Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={closeCategoryDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? "Edit Category" : "Add New Category"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Category Name"
              name="name"
              value={categoryFormData.name}
              onChange={handleCategoryFormChange}
              error={!!categoryErrors.name}
              helperText={categoryErrors.name}
            />
            <TextField
              margin="normal"
              fullWidth
              id="description"
              label="Description"
              name="description"
              multiline
              rows={3}
              value={categoryFormData.description}
              onChange={handleCategoryFormChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCategoryDialog}>Cancel</Button>
          <Button onClick={saveCategory} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resource Dialog */}
      <Dialog
        open={resourceDialogOpen}
        onClose={closeResourceDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingResource ? "Edit Resource" : "Add New Resource"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Resource Name"
                  name="name"
                  value={resourceFormData.name}
                  onChange={handleResourceFormChange}
                  error={!!resourceErrors.name}
                  helperText={resourceErrors.name}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    id="category_id"
                    name="category_id"
                    sx={{ width: "200px" }}
                    value={resourceFormData.category_id}
                    onChange={handleResourceFormChange}
                    label="Category"
                  >
                    <MenuItem value="">
                      <em>Uncategorized</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="description"
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={resourceFormData.description}
                  onChange={handleResourceFormChange}
                />
              </Grid>

              {!editingResource && (
                <Grid item xs={12}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="file_path"
                    label="File Path (in resources directory)"
                    name="file_path"
                    value={resourceFormData.file_path}
                    onChange={handleResourceFormChange}
                    error={!!resourceErrors.file_path}
                    helperText={
                      resourceErrors.file_path ||
                      "Enter the file name including extension (e.g., manual.pdf)"
                    }
                  />
                  <FormHelperText>
                    The file must already exist in the resources directory
                  </FormHelperText>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResourceDialog}>Cancel</Button>
          <Button onClick={saveResource} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ResourceManagementPage;
